import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FieldError } from "../../components/common/States";
import { useAuth, homeFor } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

const MfaStep = ({ challenge, onDone, onCancel }) => {
  const { authenticate } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { onDone(await authenticate("/auth/mfa/verify", { challenge_id: challenge.challenge_id, code })); }
    catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/mfa/resend", { challenge_id: challenge.challenge_id }); toast.success("Nouveau code envoyé"); }
    catch (err) { setError(formatApiError(err)); }
  };
  return (
    <form onSubmit={verify} className="space-y-4" noValidate data-testid="mfa-form">
      <p className="flex items-start gap-2 text-sm text-slate-600"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{challenge.email_failed
        ? <span data-testid="mfa-email-failed">L'envoi du code par email a échoué. Saisissez un de vos codes de secours (format XXXX-XXXX) ou réessayez l'envoi.</span>
        : <span>Un code à 6 chiffres vient d'être envoyé à <b data-testid="mfa-email-hint">{challenge.email_hint}</b>. Il est valable 10 minutes. Vous pouvez aussi utiliser un code de secours.</span>}</p>
      <div><Label htmlFor="mfa-code">Code de vérification ou code de secours</Label>
        <Input id="mfa-code" autoComplete="one-time-code" maxLength={9} className="mt-1.5 text-center font-mono text-lg tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^0-9A-Z-]/g, ""))} data-testid="mfa-code-input" /></div>
      <FieldError>{error && <span data-testid="mfa-error">{error}</span>}</FieldError>
      <Button type="submit" className="h-11 w-full rounded-full" disabled={loading || !/^(\d{6}|[0-9A-Z]{4}-[0-9A-Z]{4})$/.test(code)} data-testid="mfa-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Vérifier</Button>
      <div className="flex justify-between text-sm">
        <button type="button" className="text-blue-600" onClick={resend} data-testid="mfa-resend">Renvoyer un code</button>
        <button type="button" className="text-slate-500" onClick={onCancel} data-testid="mfa-cancel">Annuler</button>
      </div>
    </form>
  );
};

export const AuthCard = ({ title, subtitle, children, footer }) => (
  <div className="mx-auto flex max-w-md flex-col px-4 py-16">
    <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
    {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
    <div className="card-surface mt-8 p-6 sm:p-8">{children}</div>
    {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
  </div>
);

export default function Login() {
  const { user, authenticate } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  if (user) return <Navigate to={homeFor(user)} replace />;
  const done = (u) => navigate(params.get("next") || homeFor(u), { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email || !f.password) { setError("Email et mot de passe requis"); return; }
    setLoading(true); setError("");
    try {
      const u = await authenticate("/auth/login", f);
      if (u.mfa_required) { setChallenge(u); setF({ ...f, password: "" }); } else { done(u); }
    } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };

  if (challenge) {
    return <AuthCard title="Vérification en deux étapes" subtitle="Connexion à l'administration VOXA AFRIKA."><MfaStep challenge={challenge} onDone={done} onCancel={() => setChallenge(null)} /></AuthCard>;
  }

  return (
    <AuthCard title="Connexion" subtitle="Accédez à votre espace usager, entreprise ou administration."
      footer={<>Pas encore de compte ? <Link to="/inscription" className="font-medium text-blue-600" data-testid="login-register-link">Créer un compte usager</Link> · <Link to="/inscription-entreprise" className="font-medium text-blue-600" data-testid="login-company-register-link">Espace entreprise</Link></>}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div><Label htmlFor="email">Email</Label><Input id="email" type="email" className="mt-1.5" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div><Label htmlFor="password">Mot de passe</Label><Input id="password" type="password" className="mt-1.5" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        <FieldError>{error && <span data-testid="login-error">{error}</span>}</FieldError>
        {params.get("reset") && <p className="text-sm text-emerald-700" data-testid="login-reset-success">Mot de passe modifié. Vous pouvez vous connecter.</p>}
        <Link to="/mot-de-passe-oublie" className="block text-right text-sm text-blue-600" data-testid="login-forgot-password-link">Mot de passe oublié ?</Link>
        <Button type="submit" className="h-11 w-full rounded-full" disabled={loading} data-testid="login-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Se connecter
        </Button>
      </form>
    </AuthCard>
  );
}
